import { FC, ReactNode, useMemo } from 'react';
import { Avatar, Descriptions, Space, Tag, Typography } from 'antd';
import { BsLinkedin } from 'react-icons/bs';
import { FaSquareXTwitter } from 'react-icons/fa6';
import {
    TbBuilding,
    TbCashBanknote,
    TbExternalLink,
    TbLink,
    TbMail,
    TbMapPin,
    TbPhone,
    TbUser,
    TbUserScan,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { CompanyExecutive } from '@/queries';
import { EmptySection, FinancialValue, ListRecordDetailDrawer } from '@/shared';
import { dayjs, resolveCorporateLogo } from '@/utilities';

export interface ExecutiveCompanyContext {
    logoUrl?: string;
    name: string;
    symbol?: string;
    website?: string;
}

interface ExecutiveDetailDrawerProps {
    company: ExecutiveCompanyContext;
    executive?: CompanyExecutive;
    executives: CompanyExecutive[];
    onExecutiveChange: (executive?: CompanyExecutive) => void;
    onOpenChange: (open: boolean) => void;
    open: boolean;
    profileKind?: 'executive' | 'person';
    showCompensation?: boolean;
}

const safeUrl = (value?: string) => {
    if (!value) {
        return undefined;
    }

    try {
        const url = new URL(/^[a-z][a-z\d+.-]*:\/\//i.test(value) ? value : `https://${value}`);

        return ['http:', 'https:'].includes(url.protocol) ? url.toString() : undefined;
    } catch {
        return undefined;
    }
};

const normalizedCurrency = (currency?: string) =>
    /^[A-Z]{3}$/.test(currency?.toLocaleUpperCase() ?? '') ? currency?.toLocaleUpperCase() : 'USD';

const formatDate = (value?: string) => (value && dayjs(value).isValid() ? dayjs(value).format('MMM D, YYYY') : value);

const ExecutiveSection: FC<{ children: ReactNode; title: string }> = ({ children, title }) => {
    const { token } = useAntDesignTheme();

    return (
        <section>
            <Typography.Title level={5} style={{ marginBottom: '0.5rem' }}>
                {title}
            </Typography.Title>
            <div
                style={{
                    border: `1px dashed ${token.colorBorder}`,
                    borderRadius: token.borderRadiusLG,
                    padding: '0.75rem 1rem',
                }}
            >
                {children}
            </div>
        </section>
    );
};

const ExecutiveOverview: FC<{
    company: ExecutiveCompanyContext;
    executive: CompanyExecutive;
    profileKind: 'executive' | 'person';
}> = ({ company, executive, profileKind }) => {
    const { token } = useAntDesignTheme();
    const age = executive.yearBorn ? dayjs().year() - executive.yearBorn : undefined;
    const contactRows = [executive.email, executive.phone, executive.location].filter(Boolean);
    const companyWebsite = safeUrl(company.website);

    return (
        <Space direction='vertical' size='large' style={{ width: '100%' }}>
            <ExecutiveSection title={profileKind === 'executive' ? 'Executive Profile' : 'Person Profile'}>
                <Descriptions column={1} size='small' colon={false}>
                    {executive.title && <Descriptions.Item label='Current role'>{executive.title}</Descriptions.Item>}
                    {executive.active !== undefined && (
                        <Descriptions.Item label='Status'>
                            <Tag color={executive.active ? 'success' : 'default'}>
                                {executive.active ? 'Active' : 'Inactive'}
                            </Tag>
                        </Descriptions.Item>
                    )}
                    {executive.titleSince && (
                        <Descriptions.Item label='In role since'>
                            {executive.titleSince.toLocaleString()}
                        </Descriptions.Item>
                    )}
                    {executive.gender && <Descriptions.Item label='Gender'>{executive.gender}</Descriptions.Item>}
                    {executive.yearBorn && (
                        <Descriptions.Item label='Born'>
                            {executive.yearBorn.toLocaleString()}
                            {age && age > 0 ? ` · ${age.toLocaleString()} years old` : ''}
                        </Descriptions.Item>
                    )}
                </Descriptions>
            </ExecutiveSection>

            <ExecutiveSection title='Company Association'>
                <Space align='center' size='middle'>
                    <Avatar
                        shape='square'
                        size={40}
                        src={resolveCorporateLogo(company.website, company.logoUrl)}
                        icon={<TbBuilding />}
                        style={{ border: `1px solid ${token.colorBorder}` }}
                    />
                    <div>
                        <Typography.Text strong>{company.name}</Typography.Text>
                        <div>
                            <Typography.Text type='secondary'>{company.symbol ?? 'Current company'}</Typography.Text>
                            {companyWebsite && (
                                <Typography.Link href={companyWebsite} target='_blank' style={{ marginLeft: '0.5rem' }}>
                                    Company website <TbExternalLink style={{ verticalAlign: '-0.125rem' }} />
                                </Typography.Link>
                            )}
                        </div>
                    </div>
                </Space>
            </ExecutiveSection>

            <ExecutiveSection title='Enriched Contact Details'>
                {contactRows.length ? (
                    <Descriptions column={1} size='small' colon={false}>
                        {executive.email && (
                            <Descriptions.Item label={<TbMail style={{ color: token.colorTextTertiary }} />}>
                                <Typography.Link href={`mailto:${executive.email}`}>{executive.email}</Typography.Link>
                            </Descriptions.Item>
                        )}
                        {executive.phone && (
                            <Descriptions.Item label={<TbPhone style={{ color: token.colorTextTertiary }} />}>
                                <Typography.Link href={`tel:${executive.phone}`}>{executive.phone}</Typography.Link>
                            </Descriptions.Item>
                        )}
                        {executive.location && (
                            <Descriptions.Item label={<TbMapPin style={{ color: token.colorTextTertiary }} />}>
                                {executive.location}
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                ) : (
                    <EmptySection
                        description='No verified contact fields are available from the current enrichment record.'
                        height='10rem'
                        icon={<TbUserScan />}
                        label='No Enriched Contact Data'
                    />
                )}
            </ExecutiveSection>
        </Space>
    );
};

const ExecutiveCompensationView: FC<{ executive: CompanyExecutive }> = ({ executive }) => {
    const compensation = executive.compensation;
    const currency = compensation?.currency ?? executive.currencyPay;
    const compensationRows = [
        ['Total compensation', compensation?.total],
        ['Reported pay', executive.pay],
        ['Salary', compensation?.salary],
        ['Bonus', compensation?.bonus],
        ['Stock awards', compensation?.stockAward],
        ['Option awards', compensation?.optionAward],
        ['Incentive plan compensation', compensation?.incentivePlanCompensation],
        ['Other compensation', compensation?.allOtherCompensation],
    ] as const;
    const populatedRows = compensationRows.filter(([, value]) => value !== undefined);

    if (!populatedRows.length && !compensation?.filingDate && !compensation?.acceptedDate && !compensation?.year) {
        return (
            <EmptySection
                description='No executive compensation filing is associated with this roster record.'
                height='16rem'
                icon={<TbCashBanknote />}
                label='No Compensation Data'
            />
        );
    }

    return (
        <Space direction='vertical' size='large' style={{ width: '100%' }}>
            <ExecutiveSection title='Reported Compensation'>
                <Descriptions column={1} size='small' colon={false}>
                    {populatedRows.map(([label, value]) => (
                        <Descriptions.Item key={label} label={label}>
                            <FinancialValue currency={normalizedCurrency(currency)} value={value} />
                        </Descriptions.Item>
                    ))}
                </Descriptions>
            </ExecutiveSection>
            {(compensation?.year || compensation?.filingDate || compensation?.acceptedDate) && (
                <ExecutiveSection title='Filing Context'>
                    <Descriptions column={1} size='small' colon={false}>
                        {compensation.year && (
                            <Descriptions.Item label='Compensation year'>
                                {compensation.year.toLocaleString()}
                            </Descriptions.Item>
                        )}
                        {compensation.filingDate && (
                            <Descriptions.Item label='Filing date'>
                                {formatDate(compensation.filingDate)}
                            </Descriptions.Item>
                        )}
                        {compensation.acceptedDate && (
                            <Descriptions.Item label='Accepted date'>
                                {formatDate(compensation.acceptedDate)}
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                </ExecutiveSection>
            )}
        </Space>
    );
};

const ExecutiveDetailDrawer: FC<ExecutiveDetailDrawerProps> = ({
    company,
    executive,
    executives,
    onExecutiveChange,
    onOpenChange,
    open,
    profileKind = 'executive',
    showCompensation = true,
}) => {
    const selectedIndex = Math.max(
        0,
        executive ? executives.findIndex((candidate) => candidate.id === executive.id) : 0,
    );
    const selectedExecutive = executive ?? executives[selectedIndex];
    const socialLinks = useMemo(
        () => [
            { href: safeUrl(selectedExecutive?.website), icon: <TbLink size='1rem' />, key: 'website' },
            { href: safeUrl(selectedExecutive?.linkedinUrl), icon: <BsLinkedin size='1rem' />, key: 'linkedin' },
            { href: safeUrl(selectedExecutive?.xUrl), icon: <FaSquareXTwitter size='1rem' />, key: 'x' },
        ],
        [selectedExecutive?.linkedinUrl, selectedExecutive?.website, selectedExecutive?.xUrl],
    );
    const initials = selectedExecutive?.name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toLocaleUpperCase())
        .join('');

    if (!selectedExecutive) {
        return null;
    }

    return (
        <ListRecordDetailDrawer
            avatarFallback={initials || <TbUser />}
            avatarShape='square'
            avatarSrc={selectedExecutive.avatarUrl}
            metadata={{ row: selectedIndex }}
            open={open}
            records={executives}
            setMetadata={(metadata) => {
                const row = typeof metadata.row === 'number' ? metadata.row : 0;

                onExecutiveChange(executives[row]);
            }}
            setSelectedRecord={(record) => onExecutiveChange(record as CompanyExecutive)}
            socialLinks={socialLinks}
            tabs={[
                {
                    children: (
                        <ExecutiveOverview company={company} executive={selectedExecutive} profileKind={profileKind} />
                    ),
                    key: 'overview',
                    label: (
                        <>
                            <TbUserScan style={{ marginRight: '0.25rem', verticalAlign: '-0.125rem' }} />
                            Overview
                        </>
                    ),
                },
                ...(showCompensation
                    ? [
                          {
                              children: <ExecutiveCompensationView executive={selectedExecutive} />,
                              key: 'compensation',
                              label: (
                                  <>
                                      <TbCashBanknote style={{ marginRight: '0.25rem', verticalAlign: '-0.125rem' }} />
                                      Compensation
                                  </>
                              ),
                          },
                      ]
                    : []),
            ]}
            title={selectedExecutive.name}
            onOpenChange={onOpenChange}
        />
    );
};

export default ExecutiveDetailDrawer;
